import React from 'react';
import {
    Box,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    styled
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { MuiModal } from 'ui/MuiModal';
import { useAppSelector } from 'store/hooks';
import { PerkTemplate } from '../templates/models/PerkTemplate';
import { PerkTemplateCardBase } from '../templates/PerkTemplateCardBase';
import { filterTemplates } from '../templates/filterTemplates';

const StyledMuiModal = styled(MuiModal)(({ theme }) => ({
    width: `calc(100% - ${theme.spacing(4)})`,
    maxWidth: '1280px',
    [theme.breakpoints.up('sm')]: {
        minWidth: '506px',
        maxWidth: '600px'
    },
    [theme.breakpoints.up('md')]: {
        minWidth: '806px',
        maxWidth: '900px'
    },
    [theme.breakpoints.up('lg')]: {
        minWidth: '1120px'
    }
}));

interface TemplatePickerModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (template: PerkTemplate) => void;
}

export const TemplatePickerModal: React.FC<TemplatePickerModalProps> = ({ open, onClose, onSelect }) => {
    const { templates, loading } = useAppSelector(state => state.perkTemplates);
    const [searchValue, setSearchValue] = React.useState('');

    React.useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);

    const handleSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    }, []);

    const handleClearSearch = React.useCallback(() => {
        setSearchValue('');
    }, []);

    const sortedTemplates = React.useMemo(
        () => [...templates].sort((a, b) => (a.tenantId ? 0 : 1) - (b.tenantId ? 0 : 1)),
        [templates]
    );

    const filteredTemplates = React.useMemo(
        () => filterTemplates(sortedTemplates, searchValue),
        [sortedTemplates, searchValue]
    );

    const handleSelect = React.useCallback(
        (template: PerkTemplate) => () => {
            onSelect(template);
        },
        [onSelect]
    );

    const renderTemplate = React.useCallback(
        (template: PerkTemplate) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template._id}>
                <PerkTemplateCardBase
                    template={template}
                    suggested={!template.tenantId}
                    thumbnailHeight={140}
                    onClick={handleSelect(template)}
                />
            </Grid>
        ),
        [handleSelect]
    );

    return (
        <StyledMuiModal open={open} onClose={onClose}>
            <Box sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight="bold">
                        Create Perk from Template
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search templates..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    size="small"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }
                    }}
                    sx={{ mb: 3 }}
                />
                {loading && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                )}
                {!loading && filteredTemplates.length === 0 && (
                    <Typography variant="body2" color="textSecondary" align="center" py={4}>
                        No templates found.
                    </Typography>
                )}
                {!loading && filteredTemplates.length > 0 && (
                    <Grid container spacing={2}>
                        {filteredTemplates.map(renderTemplate)}
                    </Grid>
                )}
            </Box>
        </StyledMuiModal>
    );
};
